<template>
  <div>
    <v-row no-gutters v-if="finish_comment" class="prompt-text" align="center">
      <v-spacer></v-spacer>
      <div v-if="selected_thumb === 'like'">
        <v-icon class="thumbs-button" style="margin-right: 1rem" color="#1b65f2">mdi-thumb-up</v-icon>
        <v-icon class="thumbs-button" style="margin-right: 0.375rem">mdi-thumb-down-outline</v-icon>
      </div>
      <div v-else-if="selected_thumb === 'dislike'">
        <v-icon class="thumbs-button" style="margin-right: 1rem">mdi-thumb-up-outline</v-icon>
        <v-icon class="thumbs-button" style="margin-right: 0.375rem" color="#cf321d">mdi-thumb-down</v-icon>
      </div>
    </v-row>

    <v-row no-gutters v-else-if="is_last_message" class="prompt-text" align="center">
      {{ $vuetify.locale.t('$vuetify.__ChatBubbleAi.prompt_bar_message') }}
      <v-spacer></v-spacer>
      <v-btn
        density="comfortable"
        size="small"
        variant="flat"
        icon="mdi-thumb-up-outline"
        style="margin-right: 0.625rem"
        class="thumbs-btn"
        @click="select('like')"
        aria-label="prompt-bar-like-btn"
      ></v-btn>
      <v-btn density="comfortable" size="small" variant="flat" icon="mdi-thumb-down-outline" class="thumbs-btn" @click="select('dislike')" aria-label="prompt-bar-dislike-btn"></v-btn>
    </v-row>

    <v-dialog v-if="visibility" v-model="visibility" :persistent="finish_comment ? false : true" max-width="18.1875rem" :retain-focus="false">
      <div>
        <v-row no-gutters> <v-spacer></v-spacer><v-btn variant="text" icon="mdi-close-circle-outline" dark @click="visibility = false"></v-btn> </v-row>
        <v-card flat class="comment-dialog-card">
          <div v-if="!finish_comment">
            <v-row no-gutters>
              <v-icon v-if="selected_thumb === 'like'" class="thumbs-button">mdi-thumb-up-outline</v-icon>
              <v-icon v-else-if="selected_thumb === 'dislike'" class="thumbs-button">mdi-thumb-down-outline</v-icon>
            </v-row>
            <v-row no-gutters class="comment-dialog-card-title">
              {{ $vuetify.locale.t('$vuetify.__ChatBubbleAi.comment_dialog.title') }}
            </v-row>
            <v-row no-gutters v-if="!write_comment">
              <v-btn variant="outlined" class="chat-btn text-none" :style="`color: ${config.main_color}`" style="margin-right: 0.5rem" @click="write_comment = true">
                <v-icon>mdi-check</v-icon> {{ $vuetify.locale.t('$vuetify.__ChatBubbleAi.comment_dialog.yes') }}
              </v-btn>
              <v-btn variant="outlined" class="chat-btn text-none" :style="`color: ${config.main_color}`" @click="cancel">
                <v-icon>mdi-close</v-icon> {{ $vuetify.locale.t('$vuetify.__ChatBubbleAi.comment_dialog.no') }}
              </v-btn>
            </v-row>

            <v-row no-gutters v-else>
              <v-col cols="12">
                <v-textarea
                  v-model="comment"
                  height="4.4375rem"
                  variant="outlined"
                  density="compact"
                  single-line
                  no-resize
                  hide-details
                  :maxlength="comment_max_length"
                  :placeholder="$vuetify.locale.t('$vuetify.__ChatBubbleAi.comment_dialog.placeholder')"
                ></v-textarea>
              </v-col>
              <v-col cols="12" class="remaining-char-text">
                {{ $vuetify.locale.t('$vuetify.__ChatBubbleAi.comment_dialog.remaining_character', comment_max_length - comment.length, comment_max_length) }}
              </v-col>
              <v-col cols="12">
                <v-row no-gutters>
                  <v-spacer></v-spacer>
                  <v-btn variant="outlined" class="chat-btn text-none" :style="`color: ${config.main_color}`" @click="submit">
                    <v-icon>mdi-check</v-icon> {{ $vuetify.locale.t('$vuetify.__ChatBubbleAi.comment_dialog.submit') }}
                  </v-btn>
                </v-row>
              </v-col>
            </v-row>
          </div>
          <div v-else class="comment-dialog-card-title">{{ $vuetify.locale.t('$vuetify.__ChatBubbleAi.comment_dialog.closing') }}</div>
        </v-card>
      </div>
    </v-dialog>
  </div>
</template>

<script setup>
// Import modules
import global_config from '@/../config.json'
import { defineProps, ref, watch, nextTick } from 'vue'
import { useStore } from 'vuex'

// Init modules
const store = useStore()

const { is_last_message } = defineProps({ is_last_message: Boolean })

// Data
const config = ref(global_config)
const write_comment = ref(false)
const finish_comment = ref(false)
const comment = ref('')
const comment_max_length = ref(200)
const visibility = ref(false)
const selected_thumb = ref('')

// Methods
const select = (thumb) => {
  visibility.value = false
  selected_thumb.value = thumb
  nextTick(() => {
    visibility.value = true
  })
}
const cancel = () => {
  finish_comment.value = true
  store.dispatch('sendMessageToSocket', { title: `rating_${selected_thumb.value}`, value: `rating_${selected_thumb.value}` })
  setTimeout(() => {
    visibility.value = false
  }, 2000)
}
const submit = () => {
  finish_comment.value = true
  const payload = `rating_${selected_thumb.value}_${comment.value}`
  store.dispatch('sendMessageToSocket', { title: payload, value: payload })
  setTimeout(() => {
    visibility.value = false
  }, 2000)
}

// Watch
watch(visibility, () => {
  if (visibility.value) {
    write_comment.value = false
    finish_comment.value = false
    comment.value = ''
  }
})
</script>

<style scoped>
.prompt-text {
  max-width: 16.5rem !important;
  font-weight: 400;
  font-size: 0.6875rem;
  line-height: 0.875rem;
  color: #828282;
  padding-top: 0.25rem;
}

:deep(.v-card.comment-dialog-card) {
  padding: 1rem 1.5rem;
  box-shadow: 0px 0.25rem 0.3125rem rgba(0, 0, 0, 0.14), 0px 0.0625rem 0.625rem rgba(0, 0, 0, 0.12), 0px 0.125rem 0.25rem rgba(0, 0, 0, 0.2);
  border-radius: 1rem;
}

.comment-dialog-card-title {
  font-weight: 400;
  font-size: 0.9375rem;
  line-height: 1.25rem;
  letter-spacing: 0.00625rem;
  color: #1b1b1b;
  margin: 0.5rem 0;
}

.comment-dialog-card-btn {
  border: 0.0625rem solid rgba(0, 0, 0, 0.2);
  border-radius: 0.75rem;
  padding: 0.625rem 0.75rem !important;
  font-weight: 400;
  font-size: 0.9375rem;
  line-height: 1.5rem;
  color: #e22925;
}

.remaining-char-text {
  font-weight: 400;
  font-size: 0.75rem;
  line-height: 1rem;
  letter-spacing: 0.01875rem;
  color: #828282;
  margin: 0.5rem 0 1rem 0;
}

.thumbs-btn {
  font-size: 0.75rem !important;
  color: #828282;
}

:deep(.v-textarea textarea) {
  font-weight: 400;
  font-size: 0.9375rem;
  line-height: 1.25rem;
  letter-spacing: 0.00625rem;
  margin-top: 0.5rem !important;
}

:deep(.v-text-field > .v-input__control > .v-input__slot) {
  padding: 0 0.5rem !important;
}
</style>
